import { apiClient } from '@discovr/common';

// Thin compat shim — agents/api.ts was written against the legacy `request`
// helper. These wrappers match the same signature so callers don't need changing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function request<T = any>(
  endpoint: string,
  options: { method?: string; body?: unknown; headers?: Record<string, unknown> } = {},
): Promise<T> {
  const { method = 'GET', body } = options;
  const isFormData = body instanceof FormData;
  if (method === 'GET') {
    return apiClient.get<T>(endpoint).then((r) => r.data);
  }
  if (isFormData) {
    return apiClient
      .post<T>(endpoint, body, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  }
  return apiClient
    .request<T>({ method, url: endpoint, data: body })
    .then((r) => r.data);
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ComprehensiveCampaignBrief {
  core: {
    objective: string;
    target_kpi?: string;
    target_cpa?: string;
    duration?: string;
    total_budget?: string;
  };
  brand: {
    brand_name: string;
    product_name: string;
    category?: string;
    price_point?: string;
    usp: string[];
    why_buy_instead_of_competitors?: string;
    top_competitors: string[];
    website?: string;
    brand_stage?: string;
  };
  audience: {
    age_range?: string;
    gender_split?: string;
    geography?: string;
    language?: string;
    interests: string[];
    aspirations: string[];
    pain_points: string[];
    content_consumed: string[];
    spending_power?: string;
  };
  funnel: {
    stage: string;
  };
  platforms: {
    allowed_platforms: string[];
    allowed_formats: string[];
    whitelisting_allowed?: boolean;
    paid_amplification_budget?: string;
  };
  creators: {
    preferred_tiers: string[];
    regional_creators_needed?: string;
    excluded_categories: string[];
    mandatory_creators: string[];
  };
  guardrails: {
    tone?: string;
    claims_allowed?: string;
    words_to_avoid: string[];
    legal_restrictions?: string;
    mandatory_cta?: string;
    coupon_codes_available?: boolean;
  };
  tracking: {
    pixel_installed?: boolean;
    utm_tracking?: boolean;
    coupon_tracking?: boolean;
    affiliate_tracking?: boolean;
    reporting_frequency?: string;
  };
  target_language?: string;
  currency?: string;
}

export interface ChatResponse {
  ready: boolean;
  chat_reply: string;
  strategy_playbook?: unknown;
}

export interface CampaignRequest {
  campaign_name: string;
  description: string;
  target_audience: string;
  goals: string;
  brand_name: string;
  target_language: string;
  target_platforms: string[];
  cultural_context: string;
}

export interface ScriptRequest {
  script_content: string;
  campaign_brief: {
    name: string;
    description: string;
    video_title: string;
    primary_focus: string;
    dos: string;
    donts: string;
    cta: string;
  };
  brand_guidelines: string;
  target_language: string;
  target_platforms: string[];
  cultural_context: string;
}

export interface VideoRequest {
  video_url: string;
  campaign_brief: {
    name: string;
    description: string;
    video_title: string;
    primary_focus: string;
    dos: string;
    donts: string;
    script_content?: string;
  };
  target_language: string;
  target_platforms: string[];
  cultural_context: string;
  caption?: string;
  brand_guidelines?: string;
  platform_policies?: string;
}

export interface BrandSafetyRequest {
  campaign_brief: {
    name: string;
    description: string;
  };
  script_text: string;
  post_caption: string;
  influencer_profile: {
    name: string;
    volatility_score: number;
    political_frequency: string;
    offensive_content_history: string;
  };
  brand_policies: {
    banned_keywords: string[];
    sensitive_topics: string[];
    restricted_categories: string[];
    tone_restrictions: string;
  };
  target_platforms: string[];
  video_url: string;
  thumbnail_url: string;
}

export interface ChannelAnalysisRequest {
  channel_url: string;
  banned_keywords: string[];
  sensitive_topics: string[];
  target_platforms: string[];
}

export interface NewsAnalysisRequest {
  creator_name: string;
  channel_name?: string;
}

export interface TaskResponse {
  task_id: string;
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'Processing';
  result?: unknown;
  error?: string;
}

export const aiService = {
  chatStrategist: async (data: ComprehensiveCampaignBrief): Promise<ChatResponse> => {
    return request<ChatResponse>('/ai/campaign/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  analyzeCampaign: async (data: CampaignRequest): Promise<{ task_id: string }> => {
    return request<{ task_id: string }>('/ai/campaign/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Dashboard agent endpoints (per-user "ad-hoc analyse" flow). BE mints
  // a job_id, persists to ai_dashboard_jobs, forwards to AI's typed
  // endpoint, and webhooks back fanning the SSE event ONLY to this
  // user. The job_id in the response is the SSE filter key — the
  // dashboard agent component uses it to match the readiness event.
  //
  // task_id remains the existing AI-side task identifier; checkTaskStatus
  // still works against it as the on-demand fetch when the SSE event
  // lands. (Tasks are auth'd through the same /ai/tasks proxy.)
  reviewScript: async (data: ScriptRequest): Promise<{ task_id: string; job_id: string }> => {
    return request<{ task_id: string; job_id: string }>('/dashboard/script/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  reviewVideo: async (data: VideoRequest): Promise<{ task_id: string; job_id: string }> => {
    return request<{ task_id: string; job_id: string }>('/dashboard/content/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  uploadVideo: async (formData: FormData): Promise<{ task_id: string; file_path: string }> => {
    return request<{ task_id: string; file_path: string }>('/ai/video/upload', {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': undefined,
      },
    });
  },

  auditCreator: async (data: unknown): Promise<{ task_id: string }> => {
    return request<{ task_id: string }>('/ai/brand_safety/audit-creator', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  analyzeBrandSafety: async (data: BrandSafetyRequest): Promise<{ task_id: string; job_id: string }> => {
    return request<{ task_id: string; job_id: string }>('/dashboard/brand_safety/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  checkTaskStatus: async (taskId: string): Promise<TaskResponse> => {
    return request<TaskResponse>(`/ai/tasks/${taskId}`);
  },

  analyzeNews: async (data: NewsAnalysisRequest): Promise<{ task_id: string }> => {
    return request<{ task_id: string }>('/ai/brand_safety/news-analysis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  scrapeYouTube: async (url: string): Promise<unknown> => {
    return request<unknown>(`/ai/scraper/youtube?url=${encodeURIComponent(url)}`);
  },

  scrapeInstagram: async (handle: string): Promise<unknown> => {
    return request<unknown>(`/ai/scraper/instagram?handle=${encodeURIComponent(handle)}`);
  },

  /** YouTube comment sentiment — proxied via main API (same path as other /ai routes). */
  analyzeSentiment: async (videoUrl: string, limit: number, refresh: boolean): Promise<unknown> => {
    const q = new URLSearchParams({
      url: videoUrl,
      limit: String(limit),
      ...(refresh ? { refresh: 'true' } : {}),
    });
    return request<unknown>(`/ai/sentiment/analyze?${q.toString()}`);
  },

  analyzeCampaignSentiment: async (
    urls: string[],
    maxCommentsPerVideo: number,
    refresh: boolean,
  ): Promise<unknown> => {
    return request<unknown>('/ai/sentiment/campaign', {
      method: 'POST',
      body: JSON.stringify({
        urls,
        max_comments_per_video: maxCommentsPerVideo,
        refresh,
      }),
    });
  },
};

// Brand-safety status fetch. The Go BE wraps single-resource responses in
// `{ "data": SafetyTabResponse }` per the project envelope contract, so we
// must double-unwrap (axios `.data` → BE `.data`) to get the SafetyTabResponse
// the workspace expects (`{ status, username, data, error }`).
//
// URL: unified /api/v1/roster/{id}/analytics/safety — no persona prefix.
// The legacy `/${role}/roster/…` shape 404s; role gating is done by the
// BE handler, not the URL.
export const aiAnalyzeChannel = async (
  _token: string | undefined,
  rosterEntryId: string,
  _role: 'brand' | 'admin' = 'brand',
) => {
  const res = await apiClient.get<{ data: unknown }>(
    `/roster/${encodeURIComponent(rosterEntryId)}/analytics/safety`,
  );
  return res.data.data;
};
